import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@context/AuthContext';
import { usePublicConfig } from '@context/PublicConfigContext';
import api from '@utils/api';
import { getSocket } from '@utils/socket';
import { SOCKET_EVENTS } from '../../../shared/constants';

const NotificationContext = createContext(null);

const DEBOUNCE_MS = 250;

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const { ready: publicConfigReady } = usePublicConfig();
  const [unreadCount, setUnreadCount] = useState(0);
  const debounceTimer = useRef(null);
  /** Serializes unread-count GETs so `await refreshUnreadCount()` always runs after prior fetches (avoids stale badge when marking read while socket refetch is in flight). */
  const unreadFetchChain = useRef(Promise.resolve());

  const fetchUnreadCount = useCallback(async () => {
    if (!user || !token) {
      setUnreadCount(0);
      return;
    }
    const run = async () => {
      try {
        const { data } = await api.get('/notifications/unread-count');
        setUnreadCount(typeof data.count === 'number' ? data.count : 0);
      } catch {
        /* ignore */
      }
    };
    unreadFetchChain.current = unreadFetchChain.current.then(run).catch(() => {});
    await unreadFetchChain.current;
  }, [user, token]);

  const scheduleUnreadFetch = useCallback(() => {
    if (!user || !token) return;
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchUnreadCount();
    }, DEBOUNCE_MS);
  }, [user, token, fetchUnreadCount]);

  useEffect(() => {
    if (!user || !token) {
      setUnreadCount(0);
      unreadFetchChain.current = Promise.resolve();
      return undefined;
    }
    fetchUnreadCount();
    return () => clearTimeout(debounceTimer.current);
  }, [user, token, fetchUnreadCount]);

  useEffect(() => {
    if (!user || !token || !publicConfigReady) return undefined;
    const socket = getSocket(token);
    if (!socket) return undefined;

    const bumpUnread = () => {
      scheduleUnreadFetch();
    };

    socket.on(SOCKET_EVENTS.NEW_NOTIFICATION, bumpUnread);
    socket.on(SOCKET_EVENTS.NEW_MESSAGE, bumpUnread);
    return () => {
      socket.off(SOCKET_EVENTS.NEW_NOTIFICATION, bumpUnread);
      socket.off(SOCKET_EVENTS.NEW_MESSAGE, bumpUnread);
      clearTimeout(debounceTimer.current);
    };
  }, [user, token, publicConfigReady, scheduleUnreadFetch]);

  const value = {
    unreadCount,
    refreshUnreadCount: fetchUnreadCount,
    refreshUnreadDebounced: scheduleUnreadFetch,
    setUnreadCount,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
